/**
 * One invalidation map (WEBD-07 / WEBD-17).
 *
 * Every mutation invalidates through `invalidate(queryClient, domain)`; this
 * file is the only place that decides "what depends on X". Each domain lists
 * the key prefixes (from shared/api/queryKeys.ts) whose server data that
 * kind of write can change. Over-invalidation is cheap — only mounted
 * queries refetch; the rest are just marked stale — while a missing prefix
 * leaves a page showing stale numbers, so when in doubt a prefix goes in.
 */

import type { QueryClient, QueryKey } from '@tanstack/react-query';
import {
  allocationKeys,
  apiKeyKeys,
  calculationKeys,
  costBasisKeys,
  dailyChangeKeys,
  dividendKeys,
  externalIncomeKeys,
  holdingKeys,
  incomeKeys,
  incomeProjectionKeys,
  lotKeys,
  messageKeys,
  optionsKeys,
  passkeyKeys,
  performanceKeys,
  portfolioKeys,
  rebalancerKeys,
  settlementFundKeys,
  snaptradeKeys,
  strategyKeys,
  targetProfileKeys,
  taxKeys,
  taxPaymentKeys,
  transactionKeys,
  userKeys,
  watchlistKeys,
} from './queryKeys';

/**
 * Everything derived from the position ledger (holdings + transactions):
 * a write to either can move holdings values, lots, realized/unrealized
 * gains, income, performance, allocation and the tax calculation.
 */
const POSITION_DERIVED: readonly QueryKey[] = [
  holdingKeys.all,
  portfolioKeys.all, // holdings-summary, accounts-view (both embed holdings/transactions; accounts carry cash balances)
  calculationKeys.all, // realized gains (per account and portfolio-wide)
  lotKeys.all,
  costBasisKeys.all,
  dailyChangeKeys.all,
  performanceKeys.all, // performance data + benchmark comparison
  allocationKeys.all,
  rebalancerKeys.all,
  incomeKeys.all,
  incomeProjectionKeys.all,
  dividendKeys.all, // upcoming dividends are sized by current holdings
  optionsKeys.ccCoverageAll, // free shares / open short calls
  settlementFundKeys.all, // detection scans transaction history
  taxKeys.all,
];

const INVALIDATION_MAP = {
  /** Transaction create/update/delete/classify, corporate-action resolution. */
  transactions: [transactionKeys.all, ...POSITION_DERIVED],
  /** Holding create/update/delete/convert, cost-basis source, manual lots. */
  holdings: [...POSITION_DERIVED],
  /** Lot reassignment / recalculation, realized-gains recalculation. */
  lots: [...POSITION_DERIVED],
  /** Option expire/close/assign — changes income, and assignment changes holdings, lots and tax. */
  optionResolution: [transactionKeys.all, ...POSITION_DERIVED, messageKeys.all],
  /** SnapTrade sync / refresh / link: the ledger plus connection state and the conflict messages a sync writes. */
  sync: [transactionKeys.all, ...POSITION_DERIVED, snaptradeKeys.all, messageKeys.all],
  /**
   * Linking accounts runs their first sync. Pending-accounts is left out: the
   * link modal closes right after, and refetching it would re-render first.
   */
  accountLinking: [transactionKeys.all, ...POSITION_DERIVED, snaptradeKeys.connections(), messageKeys.all],
  /** Account create/update/delete/disconnect and portfolio-level deletes. */
  accounts: [transactionKeys.all, ...POSITION_DERIVED, snaptradeKeys.all],
  /** Deleting every portfolio (Settings). */
  portfolios: [transactionKeys.all, ...POSITION_DERIVED],
  /** Settlement-fund configure/dismiss: adds/removes the virtual cash holding. */
  settlementFund: [...POSITION_DERIVED],
  /** Dismissing a detected fund only rewrites the account's settings. */
  settlementFundDismissal: [settlementFundKeys.all],
  /** Income-record writes (tax-category override, conflict resolution). */
  incomeRecords: [incomeKeys.all, calculationKeys.all, performanceKeys.all, taxKeys.all, messageKeys.all],
  /** Estimated tax payments feed the tax calculation's paid/remaining figures. */
  taxPayments: [taxPaymentKeys.all, taxKeys.all],
  /** W-2/1099/other external income feeds the tax calculation and performance. */
  externalIncome: [externalIncomeKeys.all, taxKeys.all, performanceKeys.all],
  /** User profile / settings (filing status, state, prior-year tax…). */
  settings: [userKeys.all, taxKeys.all],
  strategies: [strategyKeys.all],
  messages: [messageKeys.all],
  watchlists: [watchlistKeys.all],
  targetProfiles: [targetProfileKeys.all],
  snaptradeAdmin: [snaptradeKeys.all],
  /** Brokerage (re)authorization: connection state as shown on accounts. */
  snaptradeConnections: [snaptradeKeys.all, portfolioKeys.all],
  apiKeys: [apiKeyKeys.all],
  passkeys: [passkeyKeys.all],
} as const satisfies Record<string, readonly QueryKey[]>;

type InvalidationDomain = keyof typeof INVALIDATION_MAP;

interface InvalidateOptions {
  /** Passed through to invalidateQueries; defaults to React Query's 'active'. */
  refetchType?: 'active' | 'inactive' | 'all' | 'none';
}

/** The de-duplicated key prefixes for one or more domains. */
function prefixesFor(domains: InvalidationDomain | readonly InvalidationDomain[]): QueryKey[] {
  const list: readonly InvalidationDomain[] = typeof domains === 'string' ? [domains] : domains;
  const seen = new Set<string>();
  const prefixes: QueryKey[] = [];
  for (const domain of list) {
    for (const prefix of INVALIDATION_MAP[domain]) {
      const hash = JSON.stringify(prefix);
      if (!seen.has(hash)) {
        seen.add(hash);
        prefixes.push(prefix);
      }
    }
  }
  return prefixes;
}

/**
 * Invalidate every query whose server data a write in `domains` can change.
 * Resolves once the resulting refetches settle.
 */
export async function invalidate(
  queryClient: QueryClient,
  domains: InvalidationDomain | readonly InvalidationDomain[],
  options: InvalidateOptions = {}
): Promise<void> {
  await Promise.all(
    prefixesFor(domains).map((queryKey) =>
      queryClient.invalidateQueries({ queryKey, refetchType: options.refetchType })
    )
  );
}
